import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatPrice } from "../../lib/api";
import { Upload, X, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "", price: 0, original_price: null, description: "", short_description: "",
  category: "", size: "", technique: "Маслени бои", colors: [], style: "Класически",
  room: [], images: [], badges: ["Оригинал"], in_stock: true, featured: false,
  occasion: [], upsell_ids: [], crosssell_ids: [],
  seo_title: "", seo_description: "", seo_keywords: "",
};

export default function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data));
    api.get("/admin/products", { params: { limit: 200 } }).then(({ data }) => setAllProducts(data.items));
    if (id) api.get(`/products/${id}`).then(({ data }) => setP({ ...EMPTY, ...data }));
  }, [id]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setCSV = (k) => (e) => set(k, e.target.value.split(",").map((s) => s.trim()).filter(Boolean));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("images", [...(p.images || []), ...urls]);
      toast.success("Качено");
    } catch { toast.error("Грешка при качване"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...p, price: parseFloat(p.price) || 0 };
      if (id) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Запазено");
      navigate("/admin/products");
    } catch { toast.error("Грешка при запис"); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (
    <div><label className="block text-sm text-navy font-medium mb-1.5">{label}</label>{children}</div>
  );
  const inp = "w-full border border-stone px-3 py-2.5 outline-none focus:border-gold";

  return (
    <form onSubmit={save} data-testid="admin-product-edit">
      <button type="button" onClick={() => navigate("/admin/products")} className="flex items-center gap-2 text-charcoal/60 hover:text-gold mb-4 text-sm"><ArrowLeft size={16} /> Назад</button>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-navy">{id ? "Редакция на картина" : "Нова картина"}</h1>
        <button type="submit" disabled={saving} className="btn-gold px-6 py-2.5 rounded-sm flex items-center gap-2 text-sm" data-testid="save-product-btn"><Save size={16} /> {saving ? "Запис..." : "Запази"}</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-stone p-6 space-y-4">
            <Field label="Заглавие"><input required value={p.title} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="field-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Цена (EUR)"><input type="number" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="field-price" /></Field>
              <Field label="Размер"><input value={p.size} onChange={(e) => set("size", e.target.value)} className={inp} placeholder="40x40 см" /></Field>
            </div>
            <Field label="Кратко описание"><textarea value={p.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2} className={inp} /></Field>
            <Field label="Пълно описание (HTML)"><textarea value={p.description} onChange={(e) => set("description", e.target.value)} rows={6} className={inp} /></Field>
          </div>

          {/* images */}
          <div className="bg-white border border-stone p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg text-navy">Изображения</h2>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="image-upload-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-navy-outline px-4 py-2 rounded-sm text-sm flex items-center gap-2" data-testid="upload-image-btn"><Upload size={15} /> {uploading ? "Качване..." : "Качи"}</button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {(p.images || []).map((img, i) => (
                <div key={i} className="relative group aspect-square">
                  <img src={img} alt="" className="w-full h-full object-cover border border-stone" />
                  <button type="button" onClick={() => set("images", p.images.filter((_, k) => k !== i))} className="absolute top-1 right-1 bg-navy text-white w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`remove-image-${i}`}><X size={13} /></button>
                </div>
              ))}
            </div>
            <Field label="Или добавете URL (за всеки нов ред)">
              <input placeholder="https://..." onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.target.value.trim()) { set("images", [...(p.images || []), e.target.value.trim()]); e.target.value = ""; } } }} className={`${inp} mt-2`} data-testid="image-url-input" />
            </Field>
          </div>

          {/* up/cross sell */}
          <div className="bg-white border border-stone p-6 space-y-4">
            <h2 className="font-serif text-lg text-navy">Препоръки (Up-sell / Cross-sell)</h2>
            <Field label="Up-sell картини">
              <MultiSelect options={allProducts} value={p.upsell_ids} onChange={(v) => set("upsell_ids", v)} testid="upsell-select" />
            </Field>
            <Field label="Cross-sell картини">
              <MultiSelect options={allProducts} value={p.crosssell_ids} onChange={(v) => set("crosssell_ids", v)} testid="crosssell-select" />
            </Field>
          </div>

          {/* SEO */}
          <div className="bg-white border border-stone p-6 space-y-4">
            <h2 className="font-serif text-lg text-navy">SEO</h2>
            <Field label="SEO заглавие"><input value={p.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inp} data-testid="field-seo-title" /></Field>
            <Field label="SEO описание (meta description)"><textarea value={p.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} rows={2} className={inp} data-testid="field-seo-desc" /></Field>
            <Field label="SEO ключови думи"><input value={p.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} className={inp} placeholder="картина, маслени бои, ..." /></Field>
          </div>
        </div>

        {/* sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-stone p-6 space-y-4">
            <Field label="Категория">
              <select value={p.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="field-category">
                <option value="">— избери —</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Стил">
              <select value={p.style} onChange={(e) => set("style", e.target.value)} className={inp}>
                <option value="Класически">Класически</option><option value="Абстрактни">Абстрактни</option>
              </select>
            </Field>
            <Field label="Техника"><input value={p.technique} onChange={(e) => set("technique", e.target.value)} className={inp} /></Field>
            <Field label="Цветове (разделени със запетая)"><input value={(p.colors || []).join(", ")} onChange={setCSV("colors")} className={inp} placeholder="Синьо, Златно" /></Field>
            <Field label="Помещения (запетая)"><input value={(p.room || []).join(", ")} onChange={setCSV("room")} className={inp} placeholder="Дневна, Спалня" /></Field>
            <Field label="Етикети/баджове (запетая)"><input value={(p.badges || []).join(", ")} onChange={setCSV("badges")} className={inp} placeholder="Оригинал, Единствен екземпляр" /></Field>
            <Field label="Поводи (запетая)"><input value={(p.occasion || []).join(", ")} onChange={setCSV("occasion")} className={inp} placeholder="Нов дом, Сватба" /></Field>
          </div>
          <div className="bg-white border border-stone p-6 space-y-3">
            <label className="flex items-center gap-3 text-sm text-navy"><input type="checkbox" checked={p.in_stock} onChange={(e) => set("in_stock", e.target.checked)} className="accent-[#C6A45C] w-4 h-4" data-testid="field-instock" /> Налично</label>
            <label className="flex items-center gap-3 text-sm text-navy"><input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#C6A45C] w-4 h-4" data-testid="field-featured" /> Препоръчана (начална страница)</label>
          </div>
        </div>
      </div>
    </form>
  );
}

function MultiSelect({ options, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="border border-stone max-h-44 overflow-y-auto" data-testid={testid}>
      {options.map((o) => (
        <label key={o.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-ivory cursor-pointer">
          <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="accent-[#C6A45C]" />
          <span className="line-clamp-1 text-charcoal/80">{o.title}</span>
        </label>
      ))}
    </div>
  );
}
